// src/components/profile-detail/VerificationTab.jsx
import React from "react";
import { 
  Grid, Box, Typography, Chip, LinearProgress, Paper, Stack
} from "@mui/material";
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import InfoCard from "./components/InfoCard";

const VerificationTab = ({ profile, genderInfo }) => {
  // Check if profile has verification data
  const verificationData = profile?.verified || {};
  
  // Check individual verification status based on actual data
  const hasEducationVerification = verificationData.education || 
    (profile?.education?.highest_degree || profile?.highest_degree || profile?.institution);
  
  const hasEmploymentVerification = verificationData.employment || 
    (profile?.profession?.job_title || profile?.job_title || profile?.organization);
  
  const hasAddressVerification = verificationData.address || 
    (profile?.residence?.city || profile?.city || profile?.state || profile?.country);
  
  const hasContactVerification = verificationData.contact || 
    (profile?.contact?.phone || profile?.phone || profile?.email);
  
  const hasFamilyVerification = verificationData.family || 
    (profile?.family?.father_name || profile?.father_name || profile?.mother_name);

  // Verification items configuration
  const verificationItems = [
    { 
      key: 'cnic', label: 'CNIC', icon: <FingerprintIcon />,
      verified: verificationData.cnic || false,
      description: verificationData.cnic ? 'Government ID verified' : 'ID verification pending',
      weight: 25
    },
    { 
      key: 'education', label: 'Education', icon: <SchoolIcon />,
      verified: Boolean(hasEducationVerification),
      description: hasEducationVerification ? 'Educational documents verified' : 'Education verification pending',
      weight: 15
    },
    { 
      key: 'employment', label: 'Employment', icon: <WorkIcon />,
      verified: Boolean(hasEmploymentVerification),
      description: hasEmploymentVerification ? 'Employment details verified' : 'Employment verification pending',
      weight: 15
    },
    { 
      key: 'address', label: 'Address', icon: <LocationOnIcon />,
      verified: Boolean(hasAddressVerification),
      description: hasAddressVerification ? 'Address verified' : 'Address verification pending',
      weight: 15
    },
    { 
      key: 'contact', label: 'Contact', icon: <ContactPhoneIcon />,
      verified: Boolean(hasContactVerification),
      description: hasContactVerification ? 'Contact details verified' : 'Contact verification pending',
      weight: 15
    },
    { 
      key: 'family', label: 'Family', icon: <BadgeIcon />,
      verified: Boolean(hasFamilyVerification),
      description: hasFamilyVerification ? 'Family background verified' : 'Family verification pending',
      weight: 15
    }
  ];

  // Calculate verification score based on weighted verification items
  const calculateVerificationScore = () => {
    let totalWeight = 0;
    let earnedWeight = 0;
    
    verificationItems.forEach(item => {
      totalWeight += item.weight;
      if (item.verified) {
        earnedWeight += item.weight;
      }
    });
    
    if (totalWeight === 0) return 0;
    return Math.round((earnedWeight / totalWeight) * 100);
  };

  const verificationScore = calculateVerificationScore();
  const verifiedCount = verificationItems.filter(item => item.verified).length;
  const pendingCount = verificationItems.filter(item => !item.verified).length;
  const totalItems = verificationItems.length;

  const getVerificationStatusColor = (verified) => {
    return verified ? 'primary.light' : 'text.disabled';
  };

  const getVerificationText = (verified) => {
    return verified ? "Verified" : "Not Verified";
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <InfoCard 
          title="Document Verification"
          icon={<VerifiedIcon />}
          sx={{
            bgcolor: 'background.default',
            border: 1, borderColor: 'divider'
          }}
        >


          <Box sx={{ p: 1, pb: 2 }}>
            {/* Verification Score */}
            <Paper elevation={0} sx={{
              textAlign: 'center', mb: 4, p: 4, borderRadius: 3,
              bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider'
            }}>
              <Typography variant="h1" color="primary.light" sx={{ 
                fontWeight: 900, mb: 1,
                fontSize: { xs: '3rem', md: '4rem' }
              }}>
                {verificationScore}%
              </Typography>
              <Typography variant="h5" color="text.primary" sx={{ fontWeight: 700, mb: 3 }}>
                Verification Score
              </Typography>
              <Box sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={verificationScore} 
                  sx={{ 
                    height: 12, borderRadius: 6,
                    bgcolor: 'primary.alpha20',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'primary.light', borderRadius: 6
                    }
                  }}
                />
              </Box>
              <Typography variant="whizText2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                {verifiedCount} of {totalItems} documents verified
                {verificationScore > 0 && ` • Weighted score: ${verificationScore}%`}
              </Typography>
            </Paper>

            {/* Verification Details Grid */}
            <Grid container spacing={3}>
              {verificationItems.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.key}>
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Paper elevation={0} sx={{ 
                      p: 3, borderRadius: 2, textAlign: 'center',
                      bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider',
                      height: '100%', minHeight: '250px',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Box sx={{ 
                        width: 60, height: 60, borderRadius: '50%',
                        bgcolor: 'primary.alpha20',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 16px'
                      }}>
                        {React.cloneElement(item.icon, {
                          sx: { fontSize: 28, color: getVerificationStatusColor(item.verified) }
                        })}
                      </Box>
                      <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700, mb: 1 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        mb: 2, textAlign: 'center', lineHeight: 1.4
                      }}>
                        {item.description}
                      </Typography>
                      <Typography variant="caption" color="primary.light" sx={{ fontWeight: 600, mb: 1 }}>
                        Weight: {item.weight}%
                      </Typography>
                      <Box sx={{ mt: 'auto', pb: 1 }}>
                        <Chip 
                          icon={item.verified ? <CheckCircleIcon /> : <CancelIcon />}
                          label={getVerificationText(item.verified)}
                          size="small"
                          sx={{ 
                            bgcolor: item.verified ? 'primary.alpha20' : 'primary.alpha5',
                            color: item.verified ? 'primary.light' : 'text.disabled',
                            fontWeight: 600, border: 1, borderColor: 'divider',
                            '& .MuiChip-icon': { 
                              color: item.verified ? 'primary.light' : 'text.disabled'
                            }
                          }}
                        />
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Verification Summary */}
            <Paper elevation={0} sx={{
              p: 3, borderRadius: 2,
              bgcolor: 'primary.alpha10', border: 1, borderColor: 'divider',
              mt: 4
            }}>
              <Stack spacing={2}>
                <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700 }}>
                  Verification Summary
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      display: 'flex', alignItems: 'center', gap: 2,
                      p: 2, borderRadius: 2, bgcolor: 'primary.alpha5',
                      minHeight: '100px', height: '100%'
                    }}>
                      <Box sx={{
                        width: 40, height: 40, borderRadius: 2,
                        bgcolor: 'primary.alpha20',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <VerifiedIcon sx={{ color: 'primary.light' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="whizText2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Verified Items
                        </Typography>
                        <Typography variant="h4" color="primary.light" sx={{ fontWeight: 800 }}>
                          {verifiedCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Complete and verified
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      display: 'flex', alignItems: 'center', gap: 2,
                      p: 2, borderRadius: 2, bgcolor: 'primary.alpha5',
                      minHeight: '100px', height: '100%'
                    }}>
                      <Box sx={{
                        width: 40, height: 40, borderRadius: 2,
                        bgcolor: 'primary.alpha20',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <CancelIcon sx={{ color: 'text.disabled' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="whizText2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Pending Items
                        </Typography>
                        <Typography variant="h4" color="text.disabled" sx={{ fontWeight: 800 }}>
                          {pendingCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Requires verification
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      display: 'flex', alignItems: 'center', gap: 2,
                      p: 2, borderRadius: 2, bgcolor: 'primary.alpha5',
                      minHeight: '100px', height: '100%'
                    }}>
                      <Box sx={{
                        width: 40, height: 40, borderRadius: 2,
                        bgcolor: 'primary.alpha20',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Typography sx={{ color: 'primary.light', fontWeight: 800, fontSize: '1.2rem' }}>
                          %
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="whizText2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Score
                        </Typography>
                        <Typography variant="h4" color="primary.light" sx={{ fontWeight: 800 }}>
                          {verificationScore}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Weighted verification
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Data Status */}
                <Box sx={{ 
                  p: 2, borderRadius: 2, bgcolor: 'primary.alpha5', mt: 2
                }}>
                  <Typography variant="whizText2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                    Verification Status
                  </Typography>
                  <Typography variant="whizText2" color="text.primary">
                    {verificationScore >= 90 ? (
                      "✅ Excellent! Profile is highly verified and trustworthy."
                    ) : verificationScore >= 70 ? (
                      "✅ Good! Profile has substantial verification."
                    ) : verificationScore >= 50 ? (
                      "⚠️ Fair! Some verification completed but more needed."
                    ) : verificationScore > 0 ? (
                      "⚠️ Basic verification. Additional documents required for full trust."
                    ) : (
                      "❌ No verification completed yet. Please upload documents to verify your profile."
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </InfoCard>
      </Grid>
    </Grid>
  );
};

export default VerificationTab;